import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useTasks } from '../context/TaskContext';

const PRIORITIES = ['High', 'Medium', 'Low'];
const CATEGORIES = ['Work', 'Study', 'Personal', 'Other'];
const STATUSES = ['To Do', 'In Progress', 'Completed'];

const PRIORITY_COLORS = { High: '#ef4444', Medium: '#f59e0b', Low: '#10b981' };

const defaultForm = (status = 'To Do') => ({
  title: '', description: '', deadline: null, priority: 'Medium', category: 'Work', status,
});

const TaskModal = ({ modal, onClose }) => {
  const { addTask, updateTask } = useTasks();
  const isEdit = modal?.type === 'edit';
  const [form, setForm] = useState(() => isEdit ? { ...modal.task, deadline: modal.task.deadline ? new Date(modal.task.deadline + 'T00:00:00') : null } : defaultForm(modal?.status));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEdit) {
      setForm({ ...modal.task, deadline: modal.task.deadline ? new Date(modal.task.deadline + 'T00:00:00') : null });
    } else {
      setForm(defaultForm(modal?.status));
    }
    setErrors({});
  }, [modal]);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: '' })); };

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = 'Title is required';
    return e;
  };

  const submit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    const data = { ...form, deadline: form.deadline ? form.deadline.toISOString().slice(0, 10) : '' };
    if (isEdit) updateTask(form.id, data);
    else addTask(data);
    onClose();
  };

  if (!modal) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-md bg-white dark:bg-[#1e2235] border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl animate-scale-in max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">{isEdit ? 'Edit Task' : 'New Task'}</h2>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <X size={15} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Task Title <span className="text-red-400">*</span></label>
            <input
              type="text"
              placeholder="What needs to be done?"
              value={form.title}
              onChange={e => set('title', e.target.value)}
              onKeyDown={e => e.key === 'Enter' && submit()}
              autoFocus
              className={`w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-[#22263a] border rounded-lg text-gray-800 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 outline-none focus:border-indigo-500 transition-colors ${errors.title ? 'border-red-400' : 'border-gray-200 dark:border-gray-700'}`}
            />
            {errors.title && <p className="text-xs text-red-400 mt-1">{errors.title}</p>}
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Description</label>
            <textarea
              placeholder="Add details or notes..."
              value={form.description}
              onChange={e => set('description', e.target.value)}
              rows={3}
              className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-[#22263a] border border-gray-200 dark:border-gray-700 rounded-lg text-gray-800 dark:text-gray-100 placeholder-gray-300 dark:placeholder-gray-600 outline-none focus:border-indigo-500 transition-colors resize-none"
            />
          </div>

          {/* Priority + Category */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Priority</label>
              <div className="flex flex-col gap-1.5">
                {PRIORITIES.map(p => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => set('priority', p)}
                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-all ${form.priority === p ? 'border-current' : 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 hover:border-gray-300 dark:hover:border-gray-600'}`}
                    style={form.priority === p ? { color: PRIORITY_COLORS[p], borderColor: PRIORITY_COLORS[p], background: PRIORITY_COLORS[p] + '18' } : {}}
                  >
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: PRIORITY_COLORS[p] }} />
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Category</label>
              <div className="flex flex-col gap-1.5">
                {CATEGORIES.map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => set('category', c)}
                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-all ${form.category === c ? 'bg-indigo-500/10 border-indigo-500/50 text-indigo-400' : 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 hover:border-gray-300 dark:hover:border-gray-600'}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Deadline + Status */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Deadline</label>
              <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 dark:bg-[#22263a] border border-gray-200 dark:border-gray-700 rounded-lg focus-within:border-indigo-500 transition-colors">
                <DatePicker
                  selected={form.deadline}
                  onChange={d => set('deadline', d)}
                  placeholderText="Pick date"
                  dateFormat="MMM d, yyyy"
                  className="text-gray-800 dark:text-gray-100"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Status</label>
              <select
                value={form.status}
                onChange={e => set('status', e.target.value)}
                className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-[#22263a] border border-gray-200 dark:border-gray-700 rounded-lg text-gray-800 dark:text-gray-100 outline-none focus:border-indigo-500 transition-colors cursor-pointer"
              >
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 px-5 pb-5">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 text-sm font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700/50 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            className="flex-1 py-2.5 text-sm font-medium text-white bg-indigo-500 hover:bg-indigo-600 rounded-lg transition-all active:scale-95"
          >
            {isEdit ? 'Save Changes' : 'Add Task'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaskModal;
